// Use API routes as proxy to falcon-people
const API_BASE_URL = '/api/people';

export interface Person {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  title?: string;
  phone?: string;
  linkedin_url?: string;
  location?: string;
  department?: string;
  seniority_level?: string;
  email_status: string;
  source?: string;
  company_id?: string;
  company_role?: string;
  company?: Company;
  metadata?: string;
  created_at: string;
  updated_at: string;
  enrichment_status?: 'not_enriched' | 'enriching' | 'enriched' | 'failed' | 'skipped';
  last_enrichment_attempt?: string;
  enriched_email?: string;
  profile_image_url?: string;
  companies_house_url?: string;
  sic_keywords?: string[];
}

export interface Company {
  id: string;
  company_name: string;
  company_number: string;
  company_category?: string;
  company_status?: string;
  country_of_origin?: string;
  reg_address_line1?: string;
  reg_address_line2?: string;
  reg_address_post_town?: string;
  reg_address_county?: string;
  reg_address_country?: string;
  reg_address_post_code?: string;
  incorporation_date?: string;
  sic_text_1?: string;
  sic_text_2?: string;
  sic_text_3?: string;
  sic_text_4?: string;
  website?: string;
  employee_count_range?: string;
  created_at: string;
  updated_at: string;
}

/** Where Find People reads from: Prospeo live (one credit per page of 25) or the saved prospects table. */
export type PeopleProvider = 'prospeo' | 'saved';

/** Error thrown by the people API with the server's machine-readable code attached. */
export interface PeopleRequestError extends Error {
  code?: string;
  status?: number;
}

/** Prospeo's job-title matching modes. */
export type TitleMatchMode = 'CONTAINS' | 'EXACT' | 'SIMILAR' | 'STRICT';

/** A canonical value offered by Prospeo's suggestions endpoint. */
export interface ProspeoSuggestion {
  value: string;
  label: string;
  kind?: string;
}

export type ProspeoSuggestionType = 'location' | 'job_title' | 'industry' | 'naics' | 'sic';

/** Prospeo plan and credit balance. */
export interface ProspeoAccount {
  plan: string;
  remainingCredits: number;
  usedCredits: number;
  renewalDays: number | null;
  renewalDate: string | null;
}

export interface PersonFilters {
  provider?: PeopleProvider;
  query?: string;
  // Prospeo-only filters. Sent only when provider === 'prospeo'; the saved
  // prospects API has no equivalent and would ignore them.
  job_titles_exclude?: string[];
  title_match?: TitleMatchMode;
  location_exclude?: string[];
  company_location?: string[];
  company_location_exclude?: string[];
  industry_exclude?: string[];
  keywords_exclude?: string[];
  keywords_all?: boolean;
  company_names?: string[];
  company_domains?: string[];
  naics?: string[];
  sic?: string[];
  founded_after?: number;
  founded_before?: number;
  experience_min?: number;
  experience_max?: number;
  max_per_company?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  title?: string;
  location?: string[];
  /** Multi-select: Prospeo and the saved API both accept repeated values. */
  department?: string[];
  source?: string[];
  companyId?: string;
  emailStatus?: string[];
  industry?: string[];
  job_titles?: string[];
  // New API-supported filters
  seniority?: string[];
  companyContains?: string;
  keywords?: string[];
  companySize?: string[];
  enrichmentStatus?: string[];
  scrapeJobId?: string;
  externalAgentId?: string;
  scrapedAfter?: string;
  scrapedBefore?: string;
  page?: number;
  limit?: number;
  sortBy?: 'firstName' | 'lastName' | 'email' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface PersonSearchResponse {
  persons: Person[];
  total: number;
  limit: number;
  offset: number;
  page?: number;
  totalPages?: number;
}

export interface CompanySizeOption {
  value: string;
  label: string;
}

export interface FilterOptions {
  locations?: string[];
  job_titles?: string[];
  industries?: string[];
  seniorities?: string[];
  companySizes?: CompanySizeOption[];
  keywords?: string[];
}

export interface EnrichmentError {
  error: string;
  message: string;
  usageType?: string;
  limit?: number;
  used?: number;
  remaining?: number;
  requested?: number;
}

export interface EnrichmentData {
  success: boolean;
  message: string;
  email?: string;
  confidence?: number;
  provider?: string;
  emailType?: 'direct' | 'company_only';
  companyEmail?: string;
}

export interface EnrichmentResult {
  success: boolean;
  data?: EnrichmentData;
  error?: EnrichmentError;
  isLimitExceeded?: boolean;
}

/** Company website for a row the Prospeo pipeline stored (rawData.prospeo.{website,domain}). */
function prospeoWebsite(rawData: any): string | undefined {
  const meta = rawData?.prospeo;
  if (!meta) return undefined;
  if (typeof meta.website === 'string' && meta.website) return meta.website;
  if (typeof meta.domain === 'string' && meta.domain) return `https://${meta.domain}`;
  return undefined;
}

export class PeopleService {
  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`) as PeopleRequestError;
      error.code = typeof errorData.code === 'string' ? errorData.code : undefined;
      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  /**
   * Enrich a single prospect to get their email
   * Returns structured result with error handling for usage limits
   */
  async enrichProspect(prospectId: string, email?: string, companyEmailFallback?: boolean, signal?: AbortSignal): Promise<EnrichmentResult> {
    const response = await fetch(`${API_BASE_URL}/enrich`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: prospectId, email, companyEmailFallback }),
      signal,
    });

    if (response.status === 429) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData,
        isLimitExceeded: true,
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData,
        isLimitExceeded: false,
      };
    }

    const data = await response.json();
    return { success: data.success === true, data };
  }

  /**
   * Bulk enrich multiple prospects
   */
  async bulkEnrichProspects(prospects: { id: string; email?: string }[]): Promise<EnrichmentResult> {
    const response = await fetch(`${API_BASE_URL}/bulk-enrich`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prospects),
    });

    if (response.status === 429) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData,
        isLimitExceeded: true,
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData,
        isLimitExceeded: false,
      };
    }

    const data = await response.json();
    // Check the actual enrichment success, not just HTTP success
    return { success: data.success === true, data };
  }

  /**
   * Prospeo plan and credit balance. Free upstream and cached there; returns
   * null rather than throwing, since this is decoration, not function.
   */
  async prospeoAccount(): Promise<ProspeoAccount | null> {
    try {
      const data = await this.makeRequest('/prospeo/account');
      return data?.account ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Canonical filter values from Prospeo (locations, job titles, industries,
   * NAICS, SIC). Free on Prospeo's side, so it can run while the user types.
   * Never throws: an empty list still lets them type a value by hand.
   */
  async prospeoSuggestions(type: ProspeoSuggestionType, query: string): Promise<ProspeoSuggestion[]> {
    if (query.trim().length < 2) return [];
    try {
      const data = await this.makeRequest(`/prospeo/suggestions?type=${type}&q=${encodeURIComponent(query.trim())}`);
      return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch {
      return [];
    }
  }

  async searchPeople(filters: PersonFilters): Promise<PersonSearchResponse> {
    const params = new URLSearchParams();
    const live = filters.provider === 'prospeo';
    if (live) params.append('provider', 'prospeo');

    if (filters.query) params.append('firstName', filters.query);
    if (filters.firstName) params.append('firstName', filters.firstName);
    if (filters.lastName) params.append('lastName', filters.lastName);
    if (filters.email) params.append('email', filters.email);
    if (filters.title) params.append('title', filters.title);
    if (filters.location && filters.location.length > 0) {
      filters.location.forEach(loc => params.append('location', loc));
    }
    filters.department?.forEach(d => d && params.append('department', d));
    if (filters.source && filters.source.length > 0) {
      filters.source.forEach(src => params.append('source', src));
    }
    if (filters.companyId) params.append('companyId', filters.companyId);
    if (filters.emailStatus && filters.emailStatus.length > 0) {
      filters.emailStatus.forEach(status => params.append('emailStatus', status));
    }
    if (filters.seniority && filters.seniority.length > 0) {
      filters.seniority.forEach(s => params.append('seniority', s));
    }
    if (filters.industry && filters.industry.length > 0) {
      filters.industry.forEach(ind => params.append('industry', ind));
    }
    if (filters.job_titles && filters.job_titles.length > 0) {
      if (live) {
        // Prospeo takes every title (OR'd)
        filters.job_titles.forEach(title => params.append('job_titles', title));
      } else {
        // The saved-prospects API only supports a single titleContains; use the first selected
        params.append('titleContains', filters.job_titles[0]);
      }
    }
    if (filters.companyContains) params.append('companyContains', filters.companyContains);
    if (filters.keywords && filters.keywords.length > 0) {
      filters.keywords.forEach(kw => params.append('keywords', kw));
    }
    if (filters.companySize && filters.companySize.length > 0) {
      filters.companySize.forEach(size => params.append('companySize', size));
    }
    if (filters.enrichmentStatus && filters.enrichmentStatus.length > 0) {
      filters.enrichmentStatus.forEach(status => params.append('enrichmentStatus', status));
    }
    if (live) {
      const appendAll = (key: string, values?: string[]) => values?.forEach((v) => v && params.append(key, v));
      const appendNum = (key: string, value?: number) => {
        if (typeof value === 'number' && Number.isFinite(value)) params.append(key, String(value));
      };
      appendAll('job_titles_exclude', filters.job_titles_exclude);
      if (filters.title_match) params.append('title_match', filters.title_match);
      appendAll('location_exclude', filters.location_exclude);
      appendAll('company_location', filters.company_location);
      appendAll('company_location_exclude', filters.company_location_exclude);
      appendAll('industry_exclude', filters.industry_exclude);
      appendAll('keywords_exclude', filters.keywords_exclude);
      if (filters.keywords_all) params.append('keywords_all', 'true');
      appendAll('company_names', filters.company_names);
      appendAll('company_domains', filters.company_domains);
      appendAll('naics', filters.naics);
      appendAll('sic', filters.sic);
      appendNum('founded_after', filters.founded_after);
      appendNum('founded_before', filters.founded_before);
      appendNum('experience_min', filters.experience_min);
      appendNum('experience_max', filters.experience_max);
      appendNum('max_per_company', filters.max_per_company);
    }

    if (filters.scrapeJobId) params.append('scrapeJobId', filters.scrapeJobId);
    if (filters.externalAgentId) params.append('externalAgentId', filters.externalAgentId);
    if (filters.scrapedAfter) params.append('scrapedAfter', filters.scrapedAfter);
    if (filters.scrapedBefore) params.append('scrapedBefore', filters.scrapedBefore);

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const raw = await this.makeRequest(`?${params.toString()}`);

    const transformedPersons = (raw.data ?? raw.persons ?? raw.prospects ?? []).map((person: any) => ({
      id: person.id,
      first_name: person.firstName,
      last_name: person.lastName,
      email: person.email,
      title: person.title,
      phone: person.phone,
      linkedin_url: person.linkedinUrl,
      location: person.location,
      department: person.department,
      seniority_level: person.seniority,
      email_status: person.emailStatus,
      source: person.source,
      company_id: person.companyId,
      metadata: person.metadata,
      created_at: person.createdAt,
      updated_at: person.updatedAt,
      enrichment_status: person.enrichmentStatus,
      last_enrichment_attempt: person.lastEnrichmentAttempt,
      enriched_email: person.enrichedEmail,
      profile_image_url: person.profileImageUrl && person.profileImageUrl.includes('media.licdn.com')
        ? `/api/images/proxy?url=${encodeURIComponent(person.profileImageUrl)}`
        : person.profileImageUrl,
      companies_house_url: person.companiesHouseUrl || null,
      sic_keywords: person.sicKeywords || [],
      company: (person.linkedinCompany?.name || person.company) ? {
        id: person.linkedinCompany?.id,
        company_name: person.linkedinCompany?.name || person.company,
        company_number: undefined,
        // Rows from Prospeo carry industry on the prospect and company facts in rawData.prospeo
        company_category: person.linkedinCompany?.industry || person.industry || undefined,
        company_status: undefined,
        country_of_origin: person.linkedinCompany?.headquartersLocation,
        sic_text_1: undefined,
        sic_text_2: undefined,
        sic_text_3: undefined,
        sic_text_4: undefined,
        website: person.linkedinCompany?.website || prospeoWebsite(person.rawData),
        employee_count_range: person.linkedinCompany?.employeeCountRange || person.companySize || person.rawData?.prospeo?.employeeRange || undefined,
        created_at: person.linkedinCompany?.createdAt,
        updated_at: person.linkedinCompany?.updatedAt,
      } : undefined,
    }));

    // Handle both flat and nested pagination responses
    const pagination = raw.pagination || raw;
    return {
      persons: transformedPersons,
      total: pagination.total ?? 0,
      limit: pagination.limit ?? 50,
      offset: pagination.page ? (pagination.page - 1) * pagination.limit : 0,
      page: pagination.page ?? 1,
      totalPages: pagination.totalPages ?? 1,
    };
  }

  async getFilterOptions(type: 'locations' | 'job-titles' | 'industries' | 'seniorities' | 'company-sizes' | 'keywords'): Promise<any> {
    return this.makeRequest(`/filters/${type}`);
  }

  async getAllFilterOptions(): Promise<FilterOptions> {
    const results = await Promise.allSettled([
      this.getFilterOptions('locations'),
      this.getFilterOptions('job-titles'),
      this.getFilterOptions('industries'),
      this.getFilterOptions('seniorities'),
      this.getFilterOptions('company-sizes'),
      this.getFilterOptions('keywords'),
    ]);

    const val = (r: PromiseSettledResult<any>) => r.status === 'fulfilled' ? r.value : {};

    return {
      locations: val(results[0]).locations || [],
      job_titles: val(results[1]).job_titles || [],
      industries: val(results[2]).industries || [],
      seniorities: val(results[3]).seniorities || [],
      companySizes: val(results[4]).companySizes || [],
      keywords: val(results[5]).keywords || [],
    };
  }
}

// Industry mapping utility
export function simplifyIndustry(sicText: string | null | undefined): string {
  if (!sicText) return 'Other';

  const sicCodeMatch = sicText.match(/^(\d{5})/);
  const sicCode = sicCodeMatch ? sicCodeMatch[1] : '';
  const sector = sicCode.substring(0, 2);
  
  const industryMapping: { [key: string]: string } = {
    '01': 'Agriculture', '02': 'Agriculture', '03': 'Agriculture',
    '05': 'Mining & Energy', '06': 'Mining & Energy', '07': 'Mining & Energy', '08': 'Mining & Energy', '09': 'Mining & Energy',
    '10': 'Food & Beverage', '11': 'Food & Beverage', '12': 'Food & Beverage',
    '26': 'Technology', '27': 'Technology', '61': 'Technology', '62': 'Technology', '63': 'Technology', '72': 'Technology',
    '41': 'Construction', '42': 'Construction', '43': 'Construction',
    '46': 'Retail', '47': 'Retail',
    '64': 'Finance', '65': 'Finance', '66': 'Finance',
    '68': 'Real Estate',
    '69': 'Professional Services', '70': 'Professional Services', '71': 'Professional Services', '74': 'Professional Services',
    '73': 'Marketing & Advertising',
    '21': 'Healthcare', '75': 'Healthcare', '86': 'Healthcare', '87': 'Healthcare', '88': 'Healthcare',
    '85': 'Education',
    '55': 'Hospitality',
    '49': 'Transportation', '50': 'Transportation', '51': 'Transportation', '52': 'Transportation', '53': 'Transportation',
    '84': 'Government',
    '90': 'Media & Entertainment', '91': 'Media & Entertainment', '92': 'Media & Entertainment', '58': 'Media & Entertainment', '59': 'Media & Entertainment', '60': 'Media & Entertainment',
    '93': 'Sports & Recreation',
    '94': 'Non-Profit',
  };
  
  if (industryMapping[sector]) {
    return industryMapping[sector];
  }
  
  const text = sicText.toLowerCase();
  
  if (text.includes('software') || text.includes('computer') || text.includes('technology')) return 'Technology';
  if (text.includes('hospital') || text.includes('medical') || text.includes('health')) return 'Healthcare';
  if (text.includes('food') || text.includes('restaurant')) return 'Food & Beverage';
  if (text.includes('retail') || text.includes('shop')) return 'Retail';
  if (text.includes('construction') || text.includes('building')) return 'Construction';
  if (text.includes('finance') || text.includes('bank')) return 'Finance';
  if (text.includes('education') || text.includes('school')) return 'Education';
  
  return 'Other';
}

export const peopleService = new PeopleService();

